import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User, Bell, Link, Shield, Palette,
  Instagram, Youtube, Globe, Check, Plus, Trash2,
  Eye, EyeOff, Save, LogOut, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useApp, PLATFORMS } from '@/context/AppContext';
import { toast } from 'sonner';

const CONNECTED_ACCOUNTS = [
  { id: 'ca1', platform: 'instagram', handle: '@alexfit_official', brand: 'AlexFit', connected: true, status: 'active' },
  { id: 'ca2', platform: 'tiktok', handle: '@alexfit', brand: 'AlexFit', connected: true, status: 'active' },
  { id: 'ca3', platform: 'youtube', handle: 'Alex Rivera Fitness', brand: 'AlexFit', connected: true, status: 'active' },
  { id: 'ca4', platform: 'instagram', handle: '@mindset_daily_alex', brand: 'Mindset Daily', connected: true, status: 'active' },
  { id: 'ca5', platform: 'youtube', handle: 'Mindset Daily', brand: 'Mindset Daily', connected: true, status: 'active' },
  { id: 'ca6', platform: 'x', handle: '@mindsetwithalex', brand: 'Mindset Daily', connected: false, status: 'disconnected' },
];

const PlatformColors = { instagram: '#E1306C', tiktok: '#69C9D0', youtube: '#FF0000', x: '#9AA4B2', facebook: '#1877F2' };
const PlatformLabels = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', x: 'X (Twitter)', facebook: 'Facebook' };

export default function Settings() {
  const { userType } = useApp();
  const [accounts, setAccounts] = useState(CONNECTED_ACCOUNTS);
  const [notifications, setNotifications] = useState({
    publish: true, risk: true, trial: true, weekly: false, tips: true
  });
  const [name, setName] = useState('My Agency');
  const [email, setEmail] = useState('admin@reelia.io');
  const [showKey, setShowKey] = useState(false);

  const toggleAccount = (id) => {
    setAccounts(prev => prev.map(a =>
      a.id === id ? { ...a, connected: !a.connected, status: a.connected ? 'disconnected' : 'active' } : a
    ));
    const acc = accounts.find(a => a.id === id);
    toast.success(acc?.connected ? 'Account disconnected' : 'Account connected');
  };

  const handleSaveProfile = () => {
    toast.success('Profile saved');
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <Tabs defaultValue="accounts">
        <TabsList style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
          <TabsTrigger value="accounts" className="gap-1.5 text-xs"><Link size={13} /> Connected Accounts</TabsTrigger>
          <TabsTrigger value="profile" className="gap-1.5 text-xs"><User size={13} /> Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="gap-1.5 text-xs"><Bell size={13} /> Notifications</TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5 text-xs"><Shield size={13} /> Security</TabsTrigger>
        </TabsList>

        {/* === CONNECTED ACCOUNTS === */}
        <TabsContent value="accounts" className="mt-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Social Accounts</p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  {accounts.filter(a => a.connected).length} of {accounts.length} accounts connected
                </p>
              </div>
              <Button size="sm" className="gap-1.5 text-xs"
                onClick={() => toast.success('Redirecting to OAuth...')}
                style={{ background: 'hsl(var(--primary))', color: '#fff' }}
              >
                <Plus size={13} /> Connect Account
              </Button>
            </div>

            {accounts.map((account, idx) => (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.06 }}
                className="reelia-card p-4 flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${PlatformColors[account.platform]}15` }}
                >
                  <span className="text-xs font-bold" style={{ color: PlatformColors[account.platform] }}>
                    {account.platform.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{account.handle}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{PlatformLabels[account.platform]}</span>
                    <span style={{ color: 'hsl(var(--foreground-subtle))' }}>·</span>
                    <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{account.brand}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {account.connected ? (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--success))' }}>
                      <Check size={11} /> Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      <AlertCircle size={11} /> Disconnected
                    </span>
                  )}
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => toggleAccount(account.id)}
                    style={{
                      color: account.connected ? 'hsl(var(--destructive))' : 'hsl(var(--primary))',
                      border: `1px solid ${account.connected ? 'hsl(var(--destructive) / 0.3)' : 'hsl(var(--primary) / 0.3)'}`,
                    }}
                  >
                    {account.connected ? 'Disconnect' : 'Reconnect'}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* === PROFILE === */}
        <TabsContent value="profile" className="mt-4">
          <div className="reelia-card p-5 max-w-lg space-y-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold"
                style={{ background: 'hsl(var(--primary) / 0.15)', color: 'hsl(var(--primary))' }}
              >
                {userType === 'agency' ? 'AG' : 'CR'}
              </div>
              <div>
                <Button size="sm" variant="ghost" style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}>
                  Change Avatar
                </Button>
              </div>
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Name</label>
              <input className="reelia-input" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Email</label>
              <input className="reelia-input" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Account Type</label>
              <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                <span className="text-xs capitalize" style={{ color: 'hsl(var(--foreground))' }}>{userType}</span>
                <Badge className="badge-blue text-xs border rounded ml-auto">Current</Badge>
              </div>
            </div>
            <Button size="sm" onClick={handleSaveProfile}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
            >
              <Save size={13} className="mr-1" /> Save Changes
            </Button>
          </div>
        </TabsContent>

        {/* === NOTIFICATIONS === */}
        <TabsContent value="notifications" className="mt-4">
          <div className="reelia-card p-5 max-w-lg">
            <div className="space-y-4">
              {[
                { key: 'publish', label: 'Post published notifications', desc: 'When your scheduled posts go live' },
                { key: 'risk', label: 'Risk scan alerts', desc: 'When content is flagged by Risk Protector' },
                { key: 'trial', label: 'Trial reminders', desc: 'When trial is ending soon' },
                { key: 'weekly', label: 'Weekly performance digest', desc: 'Summary of all brand performance' },
                { key: 'tips', label: 'Viral tips & tricks', desc: 'Weekly content strategy tips' },
              ].map(notif => (
                <div key={notif.key} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid hsl(var(--border-muted))' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{notif.label}</p>
                    <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{notif.desc}</p>
                  </div>
                  <Switch
                    checked={notifications[notif.key]}
                    onCheckedChange={v => setNotifications(prev => ({ ...prev, [notif.key]: v }))}
                  />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* === SECURITY === */}
        <TabsContent value="security" className="mt-4">
          <div className="space-y-4 max-w-lg">
            <div className="reelia-card p-5 space-y-4">
              <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Change Password</p>
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Current Password</label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    className="reelia-input pr-8"
                    placeholder="••••••••"
                  />
                  <button className="absolute right-2.5 top-1/2 -translate-y-1/2" onClick={() => setShowKey(!showKey)}
                    style={{ color: 'hsl(var(--foreground-subtle))' }}
                  >
                    {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>New Password</label>
                <input type="password" className="reelia-input" placeholder="••••••••" />
              </div>
              <Button size="sm" onClick={() => toast.success('Password updated')}
                style={{ background: 'hsl(var(--primary))', color: '#fff' }}
              >
                Update Password
              </Button>
            </div>

            <div className="reelia-card p-5">
              <p className="text-sm font-semibold mb-1" style={{ color: 'hsl(var(--foreground))' }}>Danger Zone</p>
              <p className="text-xs mb-3" style={{ color: 'hsl(var(--foreground-muted))' }}>Irreversible actions — proceed with caution</p>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost"
                  style={{ border: '1px solid hsl(var(--destructive) / 0.3)', color: 'hsl(var(--destructive))' }}
                  onClick={() => toast.error('This would delete your account')}
                >
                  Delete Account
                </Button>
                <Button size="sm" variant="ghost"
                  style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}
                >
                  <LogOut size={13} className="mr-1" /> Sign Out
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
